import { useAtomValue } from 'jotai'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import questsIcon from '../../../assets/images/icons/buy-crypto.svg'
import rewardsIcon from '../../../assets/images/icons/cup.svg'
import { useAnal, useShareUrl } from '../../../hooks'
import { userInfoAtom } from '../../../store/atoms'
import { ECurrency, EQuestName, EQuestStatus } from '../../../types/enums'
import { trpc } from '../../../utils'
import { Button, Scrollable } from '../../atoms'
import { Money, QuestItem } from '../../molecules'
import {
  availableCoins,
  availableCoinsLabel,
  collectAllButton,
  containerStyle,
  headerStyle,
  headerWithIcon,
  rewardStyle,
  taskListStyle,
} from './index.css'
import type { QuestType } from './types'

export const RewardsView = () => {
  const { data: questsResponse } = trpc.quest.getList.useQuery()
  const requestAllRewards = trpc.quest.requestAllRewards.useMutation()
  const { t } = useTranslation()
  const userInfo = useAtomValue(userInfoAtom)
  const [quests, setQuests] = useState<QuestType[]>([])
  const [avaibleRewards, setAvailableRewards] = useState(0)
  const [avaibleRewardCoins, setAvailableRewardCoins] = useState<number>(0)
  const { logEvent } = useAnal()
  const { shareUrl } = useShareUrl()

  const onClickInviteFriend = async () => {
    await shareUrl({
      link: `${import.meta.env.VITE_TELEGRAM_BOT_URL}?start=iu_${userInfo?.id}`,
      text: t('table.label.invite', {
        name: 'I',
      }),
    })
  }

  const noRewardButtons: Partial<Record<EQuestName, React.ReactNode>> = {
    [EQuestName.SUBSCRIBE_TELEGRAM_CHANNEL]: (
      <Button
        variant='bordered'
        analytic={{
          name: 'subscribe_to_tg_channel',
        }}
        onClick={() =>
          window.open(
            import.meta.env.VITE_TELEGRAM_CHANNEL_URL as string,
            '_blank',
          )
        }
      >
        {t(
          `rewards.quest.${EQuestName.SUBSCRIBE_TELEGRAM_CHANNEL}.noRewardButton`,
        )}
      </Button>
    ),
    [EQuestName.INVITE_A_FRIEND]: (
      <Button
        variant='bordered'
        analytic={{
          name: 'invite_a_friend',
        }}
        onClick={() => {
          void onClickInviteFriend()
        }}
      >
        {t(`rewards.quest.${EQuestName.INVITE_A_FRIEND}.noRewardButton`)}
      </Button>
    ),
  }

  const onQuestUpdate = (systemName: EQuestName, quest: QuestType) => {
    if (systemName === EQuestName.DAILY_CLAIM_REWARD) {
      logEvent('collected_daily_reward')
    }
    setQuests(
      quests.map((oldQuest) =>
        oldQuest.systemName === systemName ? quest : oldQuest,
      ),
    )
  }

  useEffect(() => {
    setQuests(questsResponse || [])
  }, [questsResponse])

  useEffect(() => {
    const rewardsSum = quests.reduce(
      (sum, quest) => (quest.canReward ? sum + quest.reward : sum),
      0,
    )
    const canRewardQuests = quests.filter((quest) => quest.canReward)
    setAvailableRewards(canRewardQuests.length)
    setAvailableRewardCoins(rewardsSum)
  }, [quests])

  useEffect(() => {
    if (
      !requestAllRewards.data ||
      requestAllRewards.data.success === false ||
      requestAllRewards.data.rewarded.length === 0
    ) {
      return
    }
    const newQuestsState = quests.map((quest) => {
      const doesInclude = requestAllRewards.data.rewarded.includes(
        quest.systemName,
      )
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      if (doesInclude && quest.systemName === EQuestName.DAILY_CLAIM_REWARD) {
        logEvent('collected_daily_reward')
      }
      return doesInclude
        ? {
            ...quest,
            status: EQuestStatus.REWARDED,
            canReward: false,
          }
        : quest
    })
    setQuests(newQuestsState)
  }, [requestAllRewards.data])

  return (
    <>
      <div className={containerStyle}>
        <div className={headerStyle}>
          <span className={headerWithIcon}>
            <img
              src={rewardsIcon}
              alt=''
            />
            {t('rewards.header')}
          </span>
          <Button
            disabled={requestAllRewards.isPending}
            variant={'primary'}
            className={collectAllButton}
            hidden={!avaibleRewards}
            analytic={{
              name: 'collect_all',
            }}
            onClick={() => requestAllRewards.mutate()}
          >
            {avaibleRewards
              ? t('rewards.button.collectAll', { count: avaibleRewards })
              : t('rewards.button.noRewards')}
          </Button>
        </div>
        <div className={rewardStyle}>
          <span className={availableCoins}>
            <Money
              inBigBlinds={false}
              withIcon={true}
              iconFirst={true}
              currency={ECurrency.COIN}
            >
              {avaibleRewardCoins}
            </Money>
          </span>
          <span className={availableCoinsLabel}>
            {t('rewards.label.availableFreeCoins')}
          </span>
        </div>
        <div className={headerStyle}>
          <span className={headerWithIcon}>
            <img
              src={questsIcon}
              alt=''
            />
            {t('rewards.label.quests')}
          </span>
        </div>
        <Scrollable>
          <div className={taskListStyle}>
            {quests.map((quest) => (
              <QuestItem
                key={quest.systemName}
                quest={quest}
                noRewardButton={noRewardButtons[quest.systemName] || null}
                onUpdate={(newData) => onQuestUpdate(quest.systemName, newData)}
              />
            ))}
          </div>
        </Scrollable>
      </div>
    </>
  )
}
